import type { EventsClient } from '../events-client.js';
import { ALL_SUPPORTED_ACTIONS, IntifaceController } from '../IntifaceController.js';

/**
 * Singleton that builds and routes a per-frame haptic influence HUD display.
 */
export class HapticInfluenceDisplay {
    private static _instance: HapticInfluenceDisplay;

    private eventsClient: EventsClient | null = null;
    private lastDisplayText: string = '';

    static get instance(): HapticInfluenceDisplay {
        if (!HapticInfluenceDisplay._instance) {
            HapticInfluenceDisplay._instance = new HapticInfluenceDisplay();
        }
        return HapticInfluenceDisplay._instance;
    }

    registerEventsClient(client: EventsClient): void {
        this.eventsClient = client;
        this.lastDisplayText = '';
    }

    invalidateCache(): void {
        this.lastDisplayText = '';
    }

    tick(): void {
        if (!this.eventsClient?.setHapticDisplayText) {
            return;
        }

        const displayText = this.buildDisplayText();
        if (displayText === this.lastDisplayText) {
            return;
        }

        this.lastDisplayText = displayText;
        this.eventsClient.setHapticDisplayText(displayText);
    }

    private buildDisplayText(): string {
        const controller = IntifaceController.instance;
        if (!controller) {
            return this.buildZeroedDisplayText();
        }

        const lines: string[] = [];
        for (const action of ALL_SUPPORTED_ACTIONS) {
            const influence = controller.getInfluence(action);
            const strongestInfluence = influence.reduce((maxValue, value) => {
                const normalized = Math.max(0, Math.min(1, Math.abs(value ?? 0)));
                return Math.max(maxValue, normalized);
            }, 0);
            const percent = Math.round(strongestInfluence * 100);
            lines.push(`${action}: ${percent}%`);
        }

        return lines.join('\n');
    }

    private buildZeroedDisplayText(): string {
        return ALL_SUPPORTED_ACTIONS.map((action) => `${action}: 0%`).join('\n');
    }
}
